import { defineStore } from 'pinia'
import type { MemorialServiceDetailDTO } from '../service/mservice'
import { useLocalStorage } from '@vueuse/core'
import { computed, ref } from 'vue'

type ServiceCacheEntry = {
  data: MemorialServiceDetailDTO
  updatedAt: number
  lastAccessAt: number
}

type ServiceCacheStorage = {
  v: 1
  entries: Record<string, ServiceCacheEntry>
  order: string[]
}

const SERVICE_CACHE_TTL_MS = 10 * 24 * 60 * 60 * 1000
const SERVICE_CACHE_LRU_LIMIT = 8

const createEmptyServiceCacheStorage = (): ServiceCacheStorage => ({
  v: 1,
  entries: {},
  order: []
})

const compactServiceCacheStorage = (storage: ServiceCacheStorage, now: number): ServiceCacheStorage => {
  const entries: Record<string, ServiceCacheEntry> = { ...storage.entries }
  Object.keys(entries).forEach((id) => {
    const item = entries[id]
    if (!item || now - item.updatedAt > SERVICE_CACHE_TTL_MS) {
      delete entries[id]
    }
  })

  const order: string[] = []
  storage.order.forEach((id) => {
    if (entries[id] && !order.includes(id)) {
      order.push(id)
    }
  })

  const missingIds = Object.keys(entries).filter((id) => !order.includes(id))
  missingIds
    .sort((a, b) => (entries[a]?.lastAccessAt || 0) - (entries[b]?.lastAccessAt || 0))
    .forEach((id) => order.push(id))

  while (order.length > SERVICE_CACHE_LRU_LIMIT) {
    const evictedId = order.shift()
    if (evictedId) {
      delete entries[evictedId]
    }
  }

  return {
    v: 1,
    entries,
    order
  }
}

const parseServiceCacheStorage = (raw: string): ServiceCacheStorage => {
  if (!raw) {
    return createEmptyServiceCacheStorage()
  }
  try {
    const parsed = JSON.parse(raw) as ServiceCacheStorage | MemorialServiceDetailDTO[]
    const now = Date.now()
    if (Array.isArray(parsed)) {
      const entries: Record<string, ServiceCacheEntry> = {}
      const order: string[] = []
      parsed.forEach((item) => {
        if (!item?.id) {
          return
        }
        entries[item.id] = {
          data: item,
          updatedAt: now,
          lastAccessAt: now
        }
        order.push(item.id)
      })
      return compactServiceCacheStorage({ v: 1, entries, order }, now)
    }
    if (parsed && parsed.v === 1 && parsed.entries && parsed.order) {
      return compactServiceCacheStorage(parsed, now)
    }
  } catch {}
  return createEmptyServiceCacheStorage()
}

export const useServicesStore = defineStore('serviceList', () => {
  const storage = useLocalStorage<ServiceCacheStorage>('serviceListStore', createEmptyServiceCacheStorage(), {
    serializer: {
      read: (v) => parseServiceCacheStorage(v),
      write: (v) => JSON.stringify(v)
    }
  })

  const msgId = ref('')
  const setMsgId = (id: string) => {
    msgId.value = id
  }
  const clearMsgId = () => {
    msgId.value = ''
  }

  storage.value = compactServiceCacheStorage(storage.value, Date.now())

  const serviceList = computed(() =>
    storage.value.order
      .map((id) => storage.value.entries[id]?.data)
      .filter((item): item is MemorialServiceDetailDTO => !!item)
  )

  const addById = (service: MemorialServiceDetailDTO) => {
    if (!service?.id) {
      return
    }
    const now = Date.now()
    const newStorage: ServiceCacheStorage = {
      v: 1,
      entries: {
        ...storage.value.entries,
        [service.id]: {
          data: { ...service },
          updatedAt: now,
          lastAccessAt: now
        }
      },
      order: storage.value.order.filter((id) => id !== service.id).concat(service.id)
    }
    storage.value = compactServiceCacheStorage(newStorage, now)
  }

  const searchForItem = (id?: string) => {
    if (!id) {
      return null
    }
    const current = storage.value.entries[id]
    if (!current) {
      return null
    }
    const now = Date.now()
    if (now - current.updatedAt > SERVICE_CACHE_TTL_MS) {
      return null
    }
    return current.data || null
  }

  return {
    serviceList,
    addById,
    searchForItem,
    msgId,
    setMsgId,
    clearMsgId
  }
})
